import { isNil } from 'lodash-es'
import { ComponentProps } from 'react'

import { cn } from '@/shared/lib'

import { Icon } from '../icon'

export type Rating = 1 | 2 | 3 | 4 | 5

type StarProps = Omit<ComponentProps<'input'>, 'value' | 'onChange'> & {
  value: Rating
  selected: boolean
  checked: boolean
  onChange?: (value: Rating) => void
}

const Star = ({
  selected,
  value,
  name,
  checked,
  onChange,
  tabIndex,
}: StarProps) => {
  const label = `${value} Star${value > 1 ? 's' : ''}`

  const handleChange = () => {
    onChange?.(value)
  }

  return (
    <label className="cursor-pointer" aria-label={label} tabIndex={tabIndex}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={handleChange}
        className="sr-only"
        aria-checked={checked}
        tabIndex={-1}
      />
      <Icon
        name="star"
        size={50}
        className={cn('text-gray-300 transition-colors', {
          'text-purple-500': selected,
        })}
      />
      <span className="sr-only">{label}</span>
    </label>
  )
}

export type Props = Omit<ComponentProps<'input'>, 'value' | 'onChange'> & {
  value?: Rating | null
  onChange?: (rating: Rating) => void
  className?: string
  showValue?: boolean
}

export const StarRating = ({
  value: rating,
  onChange,
  name,
  showValue = true,
  placeholder,
  className,
  ...restProps
}: Props) => {
  const isStarSelected = (value: number) => {
    return !isNil(rating) && rating >= value
  }

  const isChecked = (value: number) => {
    return rating === value
  }

  return (
    <div className="flex flex-col items-center">
      {(() => {
        if (!showValue) return null

        if (placeholder && isNil(rating)) {
          return (
            <span className="typo-label2 mb-1 block font-normal text-gray-500">
              {placeholder}
            </span>
          )
        }

        if (!isNil(rating)) {
          return (
            <span className="typo-label2 mb-1 block font-normal text-black">
              {rating}
            </span>
          )
        }

        return null
      })()}

      <div
        className={cn('flex items-center', className)}
        role="radiogroup"
        aria-label="별점 선택"
      >
        {([1, 2, 3, 4, 5] as Rating[]).map(value => (
          <Star
            key={value}
            name={name}
            value={value}
            onChange={onChange}
            selected={isStarSelected(value)}
            checked={isChecked(value)}
            tabIndex={isChecked(value) ? 0 : -1}
            {...restProps}
          />
        ))}
      </div>
    </div>
  )
}
